import React, {useCallback,useEffect,useRef,useState} from 'react';
import api from '../api';
import ManagerNativeView from '../manager-dashboard/ManagerNativeView';
import DashboardRebuildControls,{SectionRebuildFreshness} from '../components/DashboardRebuildControls';

export const sourceTime = value => value ? new Date(/[Z+-]\d*:?\d*$/.test(value) ? value : `${value}Z`).toLocaleString('ru-RU', {timeZone:'Europe/Moscow'}) : '—';
export const errorText = e => typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Не удалось выполнить операцию. Повторите попытку.';

export default function PersonalDashboard({panelId,companyName}) {
  const [state,setState]=useState(null),[error,setError]=useState(''),[save,setSave]=useState('');
  const [refreshing,setRefreshing]=useState(false),[job,setJob]=useState(null);
  const [viewVersion,setViewVersion]=useState(0);
  const revisions=useRef({}),writes=useRef(Promise.resolve()),stopped=useRef(false),epoch=useRef(0),controller=useRef(null);
  const load=useCallback(async()=>{
    const generation=epoch.current;
    try {
      const {data}=await api.get('/tenant/manager-dashboard/current',{params:{panel_id:panelId}});
      if(stopped.current||generation!==epoch.current)return;
      revisions.current=Object.fromEntries(Object.entries(data.settings).map(([key,value])=>[key,value.revision]));
      setState(data);setViewVersion(value=>value+1);setJob(data.source.current_job||null);setError('');
    }catch(e){if(!stopped.current&&generation===epoch.current){setState(null);setError(errorText(e));}}
  },[panelId]);
  useEffect(()=>{
    const generation=epoch.current+1;epoch.current=generation;stopped.current=false;setState(null);setJob(null);setSave('');load();
    return()=>{stopped.current=true;epoch.current=generation+1;};
  },[load]);
  const refresh=useCallback(async(mode='incremental')=>{
    const generation=epoch.current;setRefreshing(true);setError('');
    try{
      await writes.current;
      if(stopped.current||generation!==epoch.current)return;
      const {data}=await api.post('/tenant/manager-dashboard/current/refresh',null,{params:{panel_id:panelId,mode}});
      if(!stopped.current&&generation===epoch.current){setJob(data.job);setSave('Обновление поставлено в очередь. Прежний снимок остаётся доступен до успешной сверки.');}
    }catch(e){if(!stopped.current&&generation===epoch.current)setError(errorText(e));}
    finally{if(!stopped.current&&generation===epoch.current)setRefreshing(false);}
  },[panelId]);
  const running=['queued','running'].includes(job?.status);
  const initialLoading=Boolean(state?.source.initial_loading);
  useEffect(()=>{
    if(!initialLoading||running||state?.snapshot?.payload)return undefined;
    const timer=setInterval(load,5000);return()=>clearInterval(timer);
  },[initialLoading,running,state?.snapshot?.payload,load]);
  useEffect(()=>{
    if(!running)return;
    let active=true;
    const timer=setInterval(async()=>{
      try{
        const {data}=await api.get(`/tenant/manager-dashboard/current/jobs/${job.id}`,{params:{panel_id:panelId}});
        if(!active||stopped.current)return;
        setJob(data);
        if(data.status==='success'){setSave('Серверное обновление завершено');await load();}
        if(data.status==='failed')setError(data.error||'Обновление не завершено; прежний снимок сохранён.');
      }catch(e){if(active&&!stopped.current)setError(errorText(e));}
    },2000);
    return()=>{active=false;clearInterval(timer);};
  },[running,job?.id,panelId,load]);
  const persist=(namespace,payload)=>{
    if(!state?.can_edit||stopped.current)return;
    const generation=epoch.current;setSave('Сохраняем на сервере…');
    writes.current=writes.current.then(async()=>{
      if(stopped.current||generation!==epoch.current)return;
      try{
        const {data}=await api.put('/tenant/manager-dashboard/current/settings',{
          namespace,payload,revision:revisions.current[namespace]||0,
        },{params:{panel_id:panelId}});
        if(stopped.current||generation!==epoch.current)return;
        revisions.current[namespace]=data.revision;setSave('Сохранено на сервере');
      }catch(e){
        if(stopped.current||generation!==epoch.current)return;
        stopped.current=true;controller.current?.setReadOnly();setSave('Изменения не сохранены');
        setError(`${errorText(e)} Перечитайте панель перед дальнейшими изменениями.`);
      }
    });
  };
  const reload=()=>{stopped.current=false;load();};
  const importSettings=async event=>{
    const file=event.target.files?.[0];if(!file||!state?.can_edit)return;
    try{
      const storage=JSON.parse(await file.text());await writes.current;
      await api.post('/tenant/manager-dashboard/current/import-settings',{storage,revisions:revisions.current},{params:{panel_id:panelId}});
      await load();setSave('Настройки прежней панели перенесены на сервер');
    }catch(e){setError(e instanceof SyntaxError?'Файл не является JSON-экспортом настроек.':errorText(e));}
    finally{event.target.value='';}
  };
  const hasData=Boolean(state?.snapshot?.payload);
  return <section className="personal-dashboard" aria-label="Персональная панель">
    <div className="personal-heading">
      <div><p className="workspace-eyebrow">Дашборд Менеджеры{companyName?` · ${companyName}`:''}</p>
        <h1>{state?.panel.name||'Персональная панель'}</h1>
        <div className="personal-freshness"><span className={`source-dot${running?' source-dot--busy':''}`} aria-hidden="true"/>
          <span>Данные на <strong>{sourceTime(state?.snapshot?.source_fact_at)}</strong></span>
          {state&&!state.can_edit&&<span className="workspace-tag">Только просмотр</span>}
          {state?.snapshot?.origin==='import'&&<span className="workspace-tag">Импортированный снимок</span>}
        </div>
      </div>
      <button className="workspace-primary" onClick={()=>refresh(!hasData&&state?.can_rebuild?'full':'incremental')} disabled={!state?.can_edit||refreshing||running||initialLoading}>
        {initialLoading?'Первоначальная загрузка…':running?'Обновление выполняется…':refreshing?'Запускаем…':!hasData&&state?.can_rebuild?'Загрузить данные группы':'Обновить данные'}
      </button>
    </div>
    {state?.can_rebuild&&hasData&&<DashboardRebuildControls kind="manager" panelId={state.panel.id} beforeStart={()=>writes.current} onComplete={async()=>{await writes.current;await load();}} disabled={refreshing||running}/>}
    <SectionRebuildFreshness rows={state?.snapshot?.section_rebuilds}/>
    {error&&<div role="alert" className="workspace-alert">{error}<button onClick={reload}>Перечитать панель</button></div>}
    {state?.source.warnings?.map((warning,index)=><div key={index} className="workspace-alert personal-warning">{warning.message}</div>)}
    {(running||save)&&<p role="status" className="personal-save-status">{running?`Обновление: ${job.status==='queued'?'ожидает запуска':'выполняется'}. `:''}{save}</p>}
    {job?.status==='failed'&&<p role="alert">{job.error||'Проверенный снимок не изменён'}</p>}
    {state&&<details className="personal-source-details"><summary>Состояние данных и настройки</summary>
      <dl className="workspace-facts"><div><dt>Публикация</dt><dd>{sourceTime(state.snapshot?.published_at)}</dd></div>
        <div><dt>Последнее успешное обновление</dt><dd>{sourceTime(state.source.last_successful_update_at)}</dd></div>
        <div><dt>Подключение</dt><dd>{state.source.token_configured?'Токен настроен':'Источник не подключён'}</dd></div>
        {job&&<div><dt>Обновление</dt><dd>{({queued:'Ожидает запуска',running:'Выполняется',success:'Завершено',failed:'Ошибка'})[job.status]||job.status}</dd></div>}
      </dl>
      <details className="workspace-settings-import"><summary>Состав клиентов в планах</summary>
        <p>История планов хранит зафиксированный состав группы. Карточки выбранного месяца и темп выполнения используют текущих активных клиентов, как в исходной панели. После изменения состава эти показатели могут отличаться от истории.</p>
        <p>Если клиент выбыл из группы, его сохранённый исторический факт остаётся с прежним временем. Новый факт для такого клиента автоматически не подтверждается.</p>
      </details>
      {state.can_edit&&state.panel.group_filter?.tag==='торлопов'&&<details className="workspace-settings-import"><summary>Перенос настроек прежней панели</summary>
        <p>Выберите JSON-экспорт из браузера с вашей прежней панелью Торлопова. Снимок HTML не содержит сохранённые в браузере заметки и правки.</p>
        <label>Файл настроек<input type="file" accept=".json,application/json" onChange={importSettings}/></label>
      </details>}
      {state.can_rebuild&&<button onClick={()=>refresh('full')} disabled={refreshing||running}>Полный пересбор</button>}
    </details>}
    {!state&&!error&&<p role="status">Загружаем панель…</p>}
    {state&&!hasData&&<div className="workspace-card" role="status"><h2>{initialLoading?'Первоначальная загрузка группы':'Данные группы ещё не загружены'}</h2>
      <p>{initialLoading?'Сервер получает документы и проверяет расчёты. Панель появится после успешной сверки. Можно закрыть страницу — сбор продолжится.':'Главный администратор может запустить получение данных из МоегоСклада кнопкой выше. Снимок другого менеджера не используется.'}</p>
      {initialLoading&&!running&&<p>Ожидает своей очереди. Одновременно выполняется один сбор.</p>}
      {initialLoading&&state.source.initial_progress&&<dl className="workspace-facts">
        <div><dt>Этап</dt><dd>{state.source.initial_progress.stage==='financial_verification'?'Финансовая сверка':'Получение документов'}</dd></div>
        <div><dt>Получено страниц</dt><dd>{state.source.initial_progress.received_pages}</dd></div>
        <div><dt>Успешных обращений</dt><dd>{state.source.initial_progress.completed_requests}</dd></div>
        <div><dt>Последнее успешное действие</dt><dd>{sourceTime(state.source.initial_progress.last_successful_action_at)}</dd></div>
      </dl>}
    </div>}
    {hasData&&<ManagerNativeView key={`${panelId}:${viewVersion}`} state={state} onSetting={persist} onRefresh={()=>refresh()}
      onImportRequested={()=>setError('Импорт снимка выполняется администратором для выбранной панели.')}
      onError={setError} onController={value=>{controller.current=value;}}/>}
  </section>;
}
